import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

/**
 * 主题模式：
 *   system —— 跟随操作系统 prefers-color-scheme（默认）
 *   light / dark —— 用户手动强制
 * 解析后的有效主题（light/dark）写到 <html data-theme>，由 globals.css 的
 * :root[data-theme='dark'] 驱动全套 token 翻转。首屏防闪烁由 index.html 的
 * 内联脚本提前设好 data-theme。
 */
export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'zxdata.theme';

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function readStoredMode(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* localStorage 不可用时忽略 */
  }
  return 'system';
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === 'light' || mode === 'dark') return mode;
  return systemPrefersDark() ? 'dark' : 'light';
}

function applyToDom(resolved: ResolvedTheme) {
  document.documentElement.dataset.theme = resolved;
  // 状态栏/地址栏配色(meta[name=theme-color])不在这里设——改由 App 的 <StatusBarColor/> 按
  // 「路由 + 主题 + 滚动」统一管理：普通页取主题色(白/#0d1117)，沉浸页顶端取 hero 深色。
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredMode()));

  // mode 变化：写 DOM + 持久化
  useEffect(() => {
    const r = resolve(mode);
    setResolved(r);
    applyToDom(r);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* 忽略 */
    }
  }, [mode]);

  // 仅在 system 模式下跟随系统主题实时变化
  useEffect(() => {
    if (mode !== 'system' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const r: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolved(r);
      applyToDom(r);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => setModeState(m), []);

  return (
    <ThemeContext.Provider value={{ mode, resolved, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme 必须在 ThemeProvider 内使用');
  return ctx;
}
